import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Globe, Moon, Sun, Smartphone, Trash2, ChevronRight, CheckCircle2, X, Monitor, Bell, Phone, CalendarCheck, BarChart3, AlertTriangle, Megaphone } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface SettingsScreenProps {
  onBack: () => void;
}
const APP_LANGUAGES = ['English', 'العربية'];
export function SettingsScreen({
  onBack
}: SettingsScreenProps) {
  const {
    isLight,
    themeMode,
    setThemeMode
  } = useTheme();
  const [appLanguage, setAppLanguage] = useState('English');
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Change phone number flow
  const [phoneFlow, setPhoneFlow] = useState<'idle' | 'enter' | 'otp' | 'done'>('idle');
  const [newPhone, setNewPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Notification preferences
  const [notifications, setNotifications] = useState<Record<string, boolean>>({
    callHandled: true,
    meetingBooked: true,
    weeklySummary: true,
    overageAlert: true,
    tips: false
  });
  const toggleNotification = (id: string) => {
    setNotifications(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const notificationSettings = [{
    id: 'callHandled',
    icon: Phone,
    label: 'Call Handled',
    desc: 'When AI answers a call for you'
  }, {
    id: 'meetingBooked',
    icon: CalendarCheck,
    label: 'Meeting Booked',
    desc: 'When a caller books a meeting'
  }, {
    id: 'weeklySummary',
    icon: BarChart3,
    label: 'Weekly Summary',
    desc: 'Usage and activity recap'
  }, {
    id: 'overageAlert',
    icon: AlertTriangle,
    label: 'Overage Alerts',
    desc: 'When you exceed your plan minutes'
  }, {
    id: 'tips',
    icon: Megaphone,
    label: 'Tips & Updates',
    desc: 'Product news and helpful tips'
  }];
  // Start resend timer when entering OTP step
  useEffect(() => {
    if (phoneFlow === 'otp') {
      setResendTimer(60);
      timerRef.current = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phoneFlow]);
  const themeOptions: Array<{
    mode: 'auto' | 'light' | 'dark';
    label: string;
    icon: React.ElementType;
    desc: string;
  }> = [{
    mode: 'auto',
    label: 'Auto',
    icon: Monitor,
    desc: 'Follow phone settings'
  }, {
    mode: 'light',
    label: 'Light',
    icon: Sun,
    desc: 'Always light'
  }, {
    mode: 'dark',
    label: 'Dark',
    icon: Moon,
    desc: 'Always dark'
  }];
  return <div className="h-full flex flex-col relative" data-id="element-2200">
      <div className={`absolute top-20 right-0 w-36 h-36 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-2201" />

      {/* Header */}
      <div className={`p-4 border-b flex items-center gap-3 sticky top-0 backdrop-blur-2xl z-10 transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/50' : 'bg-dark/80 border-white/5'}`} data-id="element-2202">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-2203">
          <ArrowLeft className="w-5 h-5" data-id="element-2204" />
        </button>
        <span className={`font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2205">
          Settings
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 pb-24 relative z-10" data-id="element-2206">
        {/* Appearance */}
        <section className="space-y-3" data-id="element-2207">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2208">
            Appearance
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl p-2 shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2209">
            <div className="grid grid-cols-3 gap-1.5" data-id="element-2210">
              {themeOptions.map(opt => {
              const Icon = opt.icon;
              const isSelected = themeMode === opt.mode;
              return <button key={opt.mode} onClick={() => setThemeMode(opt.mode)} className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-all ${isSelected ? isLight ? 'bg-vexu/10 ring-1 ring-vexu/30' : 'bg-vexu/10 ring-1 ring-vexu/30' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-2211">
                    <Icon className={`w-5 h-5 ${isSelected ? isLight ? 'text-vexu-600' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2212" />
                    <span className={`text-xs font-semibold ${isSelected ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2213">
                      {opt.label}
                    </span>
                    <span className={`text-[9px] ${isSelected ? isLight ? 'text-vexu-600' : 'text-vexu/80' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2214">
                      {opt.desc}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </section>

        {/* Language */}
        <section className="space-y-3" data-id="element-2215">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2216">
            Language
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2217">
            <button onClick={() => setShowLanguagePicker(true)} className={`w-full p-4 flex items-center justify-between transition-colors text-left ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-2218">
              <div className="flex items-center gap-3" data-id="element-2219">
                <Globe className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-2220" />
                <div data-id="element-2221">
                  <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2222">
                    App Language
                  </div>
                  <div className={`text-xs ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2223">
                    {appLanguage}
                  </div>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2224" />
            </button>
          </div>
        </section>

        {/* Notifications */}
        <section className="space-y-3" data-id="element-2225">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2226">
            Notifications
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2227">
            {notificationSettings.map((setting, i) => {
            const Icon = setting.icon;
            return <div key={setting.id} className={`flex items-center justify-between p-4 ${i < notificationSettings.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-2228">
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-2229">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-2230" />
                    <div className="min-w-0" data-id="element-2231">
                      <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2232">
                        {setting.label}
                      </div>
                      <div className={`text-xs truncate ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2233">
                        {setting.desc}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => toggleNotification(setting.id)} className={`relative w-11 h-6 rounded-full flex-shrink-0 ml-3 transition-colors duration-200 ${notifications[setting.id] ? 'bg-vexu' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-2234">
                    <motion.div layout transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} className={`absolute top-0.5 w-5 h-5 rounded-full shadow-sm ${notifications[setting.id] ? 'left-[22px] bg-white' : 'left-0.5 ' + (isLight ? 'bg-white' : 'bg-zinc-400')}`} data-id="element-2235" />
                  </button>
                </div>;
          })}
          </div>
        </section>

        {/* Account */}
        <section className="space-y-3" data-id="element-2236">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2237">
            Account
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-2238">
            <button onClick={() => setPhoneFlow('enter')} className={`w-full p-4 flex items-center justify-between transition-colors text-left ${isLight ? 'hover:bg-slate-50 border-b border-slate-100' : 'hover:bg-white/5 border-b border-white/5'}`} data-id="element-2239">
              <div className="flex items-center gap-3" data-id="element-2240">
                <Smartphone className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-2241" />
                <div data-id="element-2242">
                  <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2243">
                    Change Phone Number
                  </div>
                  <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2244">
                    Verify new number with OTP
                  </div>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2245" />
            </button>
            <button className={`w-full p-4 flex items-center justify-between transition-colors text-left ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-2246">
              <div className="flex items-center gap-3" data-id="element-2247">
                <Smartphone className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-2248" />
                <div data-id="element-2249">
                  <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2250">
                    Linked Devices
                  </div>
                  <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2251">
                    1 device connected
                  </div>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2252" />
            </button>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="space-y-3" data-id="element-2253">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-red-400' : 'text-red-500/70'}`} data-id="element-2254">
            Danger Zone
          </h3>
          <button onClick={() => setShowDeleteConfirm(true)} className={`w-full p-4 flex items-center gap-3 rounded-2xl border transition-colors ${isLight ? 'bg-white/80 border-red-100 hover:bg-red-50' : 'bg-dark-card/60 border-red-500/10 hover:bg-red-500/5'}`} data-id="element-2255">
            <Trash2 className={`w-5 h-5 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-2256" />
            <div className="text-left" data-id="element-2257">
              <div className={`text-sm font-medium ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-2258">
                Delete Account
              </div>
              <div className={`text-xs ${isLight ? 'text-red-400' : 'text-red-500/60'}`} data-id="element-2259">
                Permanently remove your account and data
              </div>
            </div>
          </button>
        </section>

        {/* App Info */}
        <div className={`text-center pt-2 ${isLight ? 'text-slate-400' : 'text-zinc-600'}`} data-id="element-2260">
          <p className="text-[11px] font-medium" data-id="element-2261">Vexu.ai v1.0.0</p>
          <p className="text-[10px] mt-0.5" data-id="element-2262">Made with ♥ in Dubai</p>
        </div>
      </div>

      {/* Language Picker Modal */}
      <AnimatePresence data-id="element-2263">
        {showLanguagePicker && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2264">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowLanguagePicker(false)} data-id="element-2265" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden max-h-[60vh] ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2266">
              <div className="p-5 pb-3 flex justify-between items-center" data-id="element-2267">
                <h3 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2268">
                  App Language
                </h3>
                <button onClick={() => setShowLanguagePicker(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-2269">
                  <X className="w-5 h-5" data-id="element-2270" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[40vh] pb-4" data-id="element-2271">
                {APP_LANGUAGES.map(lang => <button key={lang} onClick={() => {
              setAppLanguage(lang);
              setShowLanguagePicker(false);
            }} className={`w-full flex items-center justify-between px-5 py-3 transition-colors ${lang === appLanguage ? isLight ? 'bg-vexu/5' : 'bg-vexu/[0.06]' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-2272">
                    <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2273">
                      {lang}
                    </span>
                    {lang === appLanguage && <CheckCircle2 className="w-4 h-4 text-vexu" data-id="element-2274" />}
                  </button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Change Phone Number Flow */}
      <AnimatePresence data-id="element-2275">
        {phoneFlow !== 'idle' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2276">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => {
          setPhoneFlow('idle');
          setNewPhone('');
          setOtpCode('');
        }} data-id="element-2277" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2278">
              {phoneFlow === 'enter' && <>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2279">
                    <Smartphone className={`w-6 h-6 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2280" />
                  </div>
                  <div data-id="element-2281">
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2282">
                      New Phone Number
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2283">
                      Enter your new phone number. We'll send an OTP to verify
                      it.
                    </p>
                  </div>
                  <input type="tel" value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="+1 (555) 000-0000" className={`w-full px-4 py-3 rounded-xl text-sm font-medium outline-none focus:outline-none focus:ring-2 focus:ring-vexu/30 transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500'}`} data-id="element-2284" />
                  <div className="flex gap-2" data-id="element-2285">
                    <button onClick={() => {
                setPhoneFlow('idle');
                setNewPhone('');
              }} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-2286">
                      Cancel
                    </button>
                    <button onClick={() => newPhone.trim() && setPhoneFlow('otp')} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${newPhone.trim() ? 'bg-vexu text-white hover:bg-vexu-600' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-600'}`} data-id="element-2287">
                      Send OTP
                    </button>
                  </div>
                </>}

              {phoneFlow === 'otp' && <>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-2288">
                    <Smartphone className={`w-6 h-6 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2289" />
                  </div>
                  <div data-id="element-2290">
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2291">
                      Verify OTP
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2292">
                      Enter the 6-digit code sent to{' '}
                      <span className="font-semibold" data-id="element-2293">{newPhone}</span>
                    </p>
                  </div>
                  <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className={`w-full px-4 py-3 rounded-xl text-center text-2xl font-bold tracking-[0.3em] outline-none focus:outline-none focus:ring-2 focus:ring-vexu/30 transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-300' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-600'}`} data-id="element-2294" />
                  <button disabled={resendTimer > 0} onClick={() => {
              if (resendTimer === 0) {
                setResendTimer(60);
                timerRef.current = setInterval(() => {
                  setResendTimer(prev => {
                    if (prev <= 1) {
                      if (timerRef.current) clearInterval(timerRef.current);
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);
              }
            }} className={`text-xs font-medium text-center w-full ${resendTimer > 0 ? isLight ? 'text-slate-400' : 'text-zinc-500' : isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2295">
                    {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                  </button>
                  <div className="flex gap-2" data-id="element-2296">
                    <button onClick={() => setPhoneFlow('enter')} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-2297">
                      Back
                    </button>
                    <button onClick={() => otpCode.length === 6 && setPhoneFlow('done')} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${otpCode.length === 6 ? 'bg-vexu text-white hover:bg-vexu-600' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-600'}`} data-id="element-2298">
                      Verify
                    </button>
                  </div>
                </>}

              {phoneFlow === 'done' && <>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-vexu/10" data-id="element-2299">
                    <CheckCircle2 className="w-6 h-6 text-vexu" data-id="element-2300" />
                  </div>
                  <div data-id="element-2301">
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2302">
                      Number Updated!
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2303">
                      Your phone number has been changed to{' '}
                      <span className="font-semibold" data-id="element-2304">{newPhone}</span>
                    </p>
                  </div>
                  <button onClick={() => {
              setPhoneFlow('idle');
              setNewPhone('');
              setOtpCode('');
            }} className="w-full py-3 rounded-xl font-semibold text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-2305">
                    Done
                  </button>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Delete Account Confirmation */}
      <AnimatePresence data-id="element-2306">
        {showDeleteConfirm && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-2307">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} data-id="element-2308" />
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-2309">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-red-50' : 'bg-red-500/10'}`} data-id="element-2310">
                <Trash2 className={`w-6 h-6 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-2311" />
              </div>
              <div data-id="element-2312">
                <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2313">
                  Delete Account?
                </h3>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2314">
                  This will permanently delete your account, call history, and
                  all settings. This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-2" data-id="element-2315">
                <button onClick={() => setShowDeleteConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-2316">
                  Cancel
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-3 rounded-xl font-semibold text-sm bg-red-500 text-white hover:bg-red-600 transition-colors" data-id="element-2317">
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}